import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from '../../axios/axiosBaseQuery';
import {
  StoerkIdBillinInfoQuotaAPIResponse,
  StoerkIdRequestAPI,
  StoerkIdRequestRequestAPI,
  StoerkIdUsageAPIResponse,
  StripeInfo,
  WorkspaceAPI,
  WorkspaceChildrenAPI,
  WorkspaceDeviceAccessAPIResponse,
  WorkspaceUpdateAutoChargeAPI,
  UserRegistrationRequest,
  SubWorkspacesAPIResponse,
  SubWorkspaceCreateRequest,
  BillingQuotaAPI,
  WorkspaceStoresUsageAPIResponse,
} from './StoerkIdAPI.model';
import { Base64 } from '../../util/Base64';
import DeviceCalls from '../commander/api/DeviceCalls';
import { DeviceDataAPIResponse } from '../commander/api/device.model';
import Util from '../../util/Util';
import { GroupAPI } from '../groups/api/GroupAPI';
import { InvitationsAPI } from '../invitations/api/InvitationsAPI';
import { Invitation } from '../invitations/invitation.model';

const WorkspaceBaseURL = new Util().getWorkSpaceManagementServiceURL();
const BillingBaseURL = new Util().getBillingManagementServiceURL();
const StoerkIdAPI = createApi({
  reducerPath: 'stoerkId',
  baseQuery: axiosBaseQuery(),
  tagTypes: [
    'create-request',
    'cancel-self',
    'sales-team-actions',
    'workspace-actions',
    'workspace-usage',
  ],
  endpoints(build) {
    return {
      setNewRequest: build.mutation<any, StoerkIdRequestRequestAPI>({
        query: (request) => {
          return {
            url:
              process.env.NODE_ENV === 'development'
                ? `${BillingBaseURL}/stoerk/request?sendEmail=false`
                : `${BillingBaseURL}/stoerk/request`,
            method: 'post',
            data: request,
          };
        },
        invalidatesTags: ['create-request'],
      }),
      getMyRequests: build.query<StoerkIdRequestAPI[], undefined>({
        query: () => ({ url: `${BillingBaseURL}/stoerk/request/self` }),
        providesTags: ['create-request', 'cancel-self', 'sales-team-actions'],
      }),

      getMyWorkspaces: build.query<WorkspaceAPI[], undefined>({
        query: () => ({ url: `${WorkspaceBaseURL}/self` }),
        providesTags: ['cancel-self', 'workspace-actions'],
      }),
      cancelMyRequest: build.mutation<any, string>({
        query: (requestUUID) => {
          return {
            url: `${BillingBaseURL}/stoerk/request/${Base64.encode(
              requestUUID
            )}/actions/cancel`,
            method: 'post',
          };
        },
        invalidatesTags: ['cancel-self'],
      }),
      getSalesTeamRequests: build.query<StoerkIdRequestAPI[], undefined>({
        query: () => ({ url: `${BillingBaseURL}/stoerk/request` }),
        providesTags: ['create-request', 'sales-team-actions'],
      }),
      getAllWorkspace: build.query<WorkspaceAPI[] | undefined, any>({
        query: () => ({ url: `${WorkspaceBaseURL}` }),
        providesTags: ['workspace-actions'],
      }),
      getWorkspace: build.query<WorkspaceAPI | undefined, any>({
        async queryFn(workspaceUUID, _queryApi, _extraOptions, fetchWithBQ) {
          if (!workspaceUUID) {
            return { data: undefined } as any;
          }
          return await fetchWithBQ({
            url: `${WorkspaceBaseURL}/${Base64.encode(workspaceUUID)}`,
          });
        },
        providesTags: ['workspace-actions'],
      }),
      getBillingInfoFromWorkspace: build.query<
        StoerkIdBillinInfoQuotaAPIResponse,
        any
      >({
        async queryFn(workspaceUUID, _queryApi, _extraOptions, fetchWithBQ) {
          if (!workspaceUUID) {
            return { data: undefined } as any;
          }
          return await fetchWithBQ({
            url: `${BillingBaseURL}/stoerk/${Base64.encode(
              workspaceUUID
            )}/quota`,
          });
        },
        providesTags: ['workspace-actions'],
      }),
      getWorkspaceUsage: build.query<
        StoerkIdUsageAPIResponse | undefined,
        | {
            workspaceId: string;
            includeChildWorkspaces?: boolean;
            includeLists?: boolean;
          }
        | undefined
      >({
        async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
          if (!arg?.workspaceId) {
            return { data: undefined } as any;
          }
          const params: Record<string, string> = {
            includeChildWorkspaces: String(arg.includeChildWorkspaces ?? false),
            includeLists: String(arg.includeLists ?? true),
          };
          return await fetchWithBQ({
            url: `${WorkspaceBaseURL}/${Base64.encode(arg.workspaceId)}/usage`,
            params,
          });
        },
        providesTags: ['workspace-usage'],
      }),
      getWorkspaceDeviceUsage: build.query<
        DeviceDataAPIResponse[] | undefined,
        string[] | undefined
      >({
        async queryFn(deviceIds) {
          if (!deviceIds || !deviceIds.length) {
            return { data: [] } as any;
          }

          try {
            const data = await new DeviceCalls().getDevicesDataByIds(
              deviceIds.map(Base64.encode)
            );
            return { data };
          } catch (error) {
            return { error, data: undefined };
          }
        },
      }),
      getDeviceAccess: build.query<
        WorkspaceDeviceAccessAPIResponse | undefined,
        { deviceId: string; workspaceId: string }
      >({
        query: ({ deviceId, workspaceId }) => {
          return {
            url: `${WorkspaceBaseURL}/${Base64.encode(
              workspaceId
            )}/device/${Base64.encode(deviceId)}/access`,
          };
        },
        providesTags: (_result, _error, arg) => [
          {
            type: 'workspace-usage' as const,
            id: `${arg.workspaceId}-${arg.deviceId}`,
          },
        ],
      }),
      deleteDeviceInWholeWorkspace: build.mutation<
        any,
        { deviceId: string; workspaceId: string }
      >({
        query: ({ deviceId, workspaceId }) => {
          return {
            url: `${WorkspaceBaseURL}/${Base64.encode(
              workspaceId
            )}/device/${Base64.encode(deviceId)}`,
            method: 'delete',
          };
        },
        invalidatesTags: (result, error, arg) => [
          'workspace-usage',
          {
            type: 'workspace-usage',
            id: `${arg.workspaceId}-${arg.deviceId}`,
          },
        ],
      }),
      getWorkspaceStripeInfo: build.query<
        StripeInfo | undefined,
        string | undefined
      >({
        query: (workspaceUUID = '') => {
          return {
            url: `${BillingBaseURL}/stoerk/${Base64.encode(
              workspaceUUID
            )}/stripe`,
          };
        },
        providesTags: ['workspace-actions'],
      }),
      setStripeIdFromWorkspace: build.mutation<
        any,
        { workspaceId: string; stripeId: string }
      >({
        query: ({ workspaceId, stripeId }) => {
          return {
            url: `${BillingBaseURL}/stoerk/${Base64.encode(
              workspaceId
            )}/stripe`,
            method: 'put',
            data: stripeId,
            transformRequest: (data) => {
              return data;
            },
            headers: {
              'Content-Type': 'application/json',
            },
          };
        },
        invalidatesTags: ['sales-team-actions', 'workspace-actions'],
      }),
      setQuotaFromWorkspace: build.mutation<
        any,
        {
          workspaceId: string;
          quota: BillingQuotaAPI;
        }
      >({
        query: ({ workspaceId, quota }) => {
          return {
            url: `${BillingBaseURL}/stoerk/${Base64.encode(workspaceId)}/quota`,
            method: 'put',
            data: quota,
          };
        },
        invalidatesTags: ['sales-team-actions', 'workspace-actions'],
      }),

      /**
       * for security issues, the url of portal need to be authenticated by the backend
       * so we need to get the stripe id from the backend
       */
      getStripePortal: build.query<
        { url: string },
        { requestId?: string; workspaceId?: string }
      >({
        async queryFn(
          { requestId, workspaceId },
          _queryApi,
          _extraOptions,
          fetchWithBQ
        ) {
          if (!workspaceId && !requestId) {
            return { data: undefined } as any;
          }
          const params = {
            returnUrl: window.location.href,
          };
          if (requestId)
            return await fetchWithBQ({
              url: `${BillingBaseURL}/stoerk/request/${Base64.encode(
                requestId
              )}/portal`,
              method: 'post',
              params,
            });
          return await fetchWithBQ({
            url: `${BillingBaseURL}/stoerk/${Base64.encode(
              workspaceId as string
            )}/portal`,
            method: 'post',
            params,
          });
        },
        providesTags: ['workspace-actions', 'sales-team-actions'],
      }),
      addUserFromWorkspace: build.mutation<
        any,
        { email: string; workspaceId: string }
      >({
        query: ({ email, workspaceId }) => {
          return {
            url: `${WorkspaceBaseURL}/${Base64.encode(
              workspaceId
            )}/users/email`,
            method: 'post',
            data: email,
            transformRequest: (data) => {
              return data;
            },
            headers: {
              'Content-Type': 'application/json',
            },
          };
        },
        invalidatesTags: ['workspace-actions'],
      }),
      removeUserFromWorkspace: build.mutation<
        any,
        { userId: string; workspaceId: string }
      >({
        query: ({ userId, workspaceId }) => {
          return {
            url: `${WorkspaceBaseURL}/${Base64.encode(
              workspaceId
            )}/users/${Base64.encode(userId)}`,
            method: 'delete',
          };
        },
        invalidatesTags: ['workspace-actions'],
      }),
      // Not longer used
      setAutoChargeFromWorkspace: build.mutation<
        void,
        { workspaceId: string; autocharge: boolean }
      >({
        query: ({ workspaceId, autocharge }) => {
          return {
            url: `${BillingBaseURL}/stoerk/${Base64.encode(
              workspaceId
            )}/autocharge`,
            method: 'post',
            data: { autocharge } as WorkspaceUpdateAutoChargeAPI,
          };
        },
        invalidatesTags: ['workspace-actions'],
      }),
      setQuotaFromRequest: build.mutation<
        any,
        {
          requestId: string;
          quota: {
            userQuota: number;
            deviceQuota: number;
            pricingDetails: { text: string };
          };
        }
      >({
        query: ({ requestId, quota }) => {
          return {
            url: `${BillingBaseURL}/stoerk/request/${Base64.encode(
              requestId
            )}/action/sales/update_quota`,
            method: 'post',
            data: quota,
          };
        },
        invalidatesTags: ['sales-team-actions'],
      }),
      setStripeIdAndActivateFromRequest: build.mutation<
        any,
        { requestId: string; stripeId: string }
      >({
        query: ({ requestId, stripeId }) => {
          return {
            url: `${BillingBaseURL}/stoerk/request/${Base64.encode(
              requestId
            )}/action/sales/link_and_activate`,
            method: 'post',
            data: {
              stripeUserId: stripeId,
            },
          };
        },
        invalidatesTags: ['sales-team-actions', 'workspace-actions'],
      }),
      rejectRequest: build.mutation<any, string>({
        query: (requestUUID) => {
          return {
            url: `${BillingBaseURL}/stoerk/request/${Base64.encode(
              requestUUID
            )}/action/sales/deny`,
            method: 'post',
          };
        },
        invalidatesTags: ['sales-team-actions'],
      }),
      setAutoChargeFromRequest: build.mutation<
        void,
        { requestId: string; autocharge: boolean }
      >({
        query: ({ requestId, autocharge }) => {
          return {
            url: `${BillingBaseURL}/stoerk/request/${Base64.encode(
              requestId
            )}/action/user/autocharge`,
            method: 'post',
            data: { autocharge } as WorkspaceUpdateAutoChargeAPI,
          };
        },
        invalidatesTags: ['create-request'],
      }),
      getArticleNumbersFromWorkspace: build.query({
        query: ({ workspaceId }) => {
          return {
            url: `${WorkspaceBaseURL}/msm/${Base64.encode(
              workspaceId
            )}/articlenumbers/`,
            method: 'get',
          };
        },
      }),
      registerUserRequest: build.mutation<
        any,
        { workspaceId: string; registrationData: UserRegistrationRequest }
      >({
        query: ({ workspaceId, registrationData }) => {
          return {
            url: `${WorkspaceBaseURL}/${Base64.encode(
              workspaceId
            )}/register/self-request`,
            method: 'post',
            data: registrationData,
          };
        },
        invalidatesTags: ['workspace-actions'],
      }),
      inviteUser: build.mutation<
        any,
        { workspaceId: string; registrationData: Partial<Invitation> }
      >({
        query: ({ workspaceId, registrationData }) => {
          return {
            url: `${WorkspaceBaseURL}/${Base64.encode(
              workspaceId
            )}/register/service`,
            method: 'post',
            data: registrationData,
          };
        },
        invalidatesTags: ['workspace-actions'],
        async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(InvitationsAPI.util.invalidateTags(['Invitations']));
          } catch (error) {
            console.error('Invalidation failed', error);
          }
        },
      }),
      linkGroupToWorkspace: build.mutation({
        query: ({ groupId, workspaceId }) => {
          return {
            url: `${WorkspaceBaseURL}/${Base64.encode(workspaceId)}/groups`,
            method: 'post',
            data: Base64.encode(groupId),
            transformRequest: (data: string) => {
              return data;
            },
            headers: {
              'Content-Type': 'application/json',
            },
          };
        },
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            // Wait for the mutation to succeed
            await queryFulfilled;

            dispatch(GroupAPI.util.invalidateTags(['group-data']));
          } catch (error) {
            console.error('Invalidation failed', error);
          }
        },
      }),
      unlinkGroupToWorkspace: build.mutation({
        query: ({ groupId, workspaceId }) => {
          return {
            url: `${WorkspaceBaseURL}/${Base64.encode(
              workspaceId
            )}/groups/${Base64.encode(groupId)}`,
            method: 'delete',
            transformRequest: (data: string) => {
              return data;
            },
            headers: {
              'Content-Type': 'application/json',
            },
          };
        },
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            // Wait for the mutation to succeed
            await queryFulfilled;

            dispatch(GroupAPI.util.invalidateTags(['group-data']));
          } catch (error) {
            console.error('Invalidation failed', error);
          }
        },
      }),

      // --- SubWorkspace (End Customer Spaces) endpoints ---

      getWorkspaceDescendants: build.query<WorkspaceChildrenAPI[], string>({
        query: (workspaceId) => ({
          url: `${WorkspaceBaseURL}/${Base64.encode(workspaceId)}/descendants`,
        }),
        providesTags: ['workspace-actions'],
      }),
      getSubWorkspaces: build.query<SubWorkspacesAPIResponse, string>({
        query: (workspaceId) => ({
          url: `${BillingBaseURL}/${Base64.encode(workspaceId)}/subworkspaces`,
        }),
        providesTags: ['workspace-actions'],
      }),
      createSubWorkspace: build.mutation<
        WorkspaceAPI,
        { workspaceId: string; formData: SubWorkspaceCreateRequest }
      >({
        query: ({ workspaceId, formData }) => ({
          url: `${BillingBaseURL}/${Base64.encode(
            workspaceId
          )}/subworkspaces/create`,
          method: 'post',
          data: formData,
        }),
        transformResponse: (response: WorkspaceAPI) => ({
          ...response,
          id: Base64.decode(response.id),
        }),
        invalidatesTags: ['workspace-actions'],
      }),
      updateSubWorkspaceQuota: build.mutation<
        null,
        {
          workspaceId: string;
          subWorkspaceId: string;
          quota: { users: number; devices: number };
        }
      >({
        query: ({ workspaceId, subWorkspaceId, quota }) => ({
          url: `${BillingBaseURL}/${Base64.encode(
            workspaceId
          )}/subworkspaces/${Base64.encode(subWorkspaceId)}/quota`,
          method: 'put',
          data: quota,
        }),
        invalidatesTags: ['workspace-actions'],
      }),
      updateSubWorkspaceModules: build.mutation<
        null,
        {
          workspaceId: string;
          subWorkspaceId: string;
          modules: string[];
        }
      >({
        query: ({ workspaceId, subWorkspaceId, modules }) => ({
          url: `${BillingBaseURL}/${Base64.encode(
            workspaceId
          )}/subworkspaces/${Base64.encode(subWorkspaceId)}/modules`,
          method: 'put',
          data: modules,
        }),
        invalidatesTags: ['workspace-actions'],
      }),
      removeSubWorkspace: build.mutation<
        void,
        { workspaceId: string; subWorkspaceId: string }
      >({
        query: ({ workspaceId, subWorkspaceId }) => ({
          url: `${BillingBaseURL}/${Base64.encode(
            workspaceId
          )}/subworkspaces/${Base64.encode(subWorkspaceId)}`,
          method: 'delete',
        }),
        invalidatesTags: ['workspace-actions'],
      }),
      getParentWorkspaceInfo: build.query<any, string>({
        query: (workspaceId) => ({
          url: `${BillingBaseURL}/${Base64.encode(workspaceId)}/parent`,
        }),
        providesTags: ['workspace-actions'],
      }),
      getWorkspaceStoresUsage: build.query<
        WorkspaceStoresUsageAPIResponse | null,
        | {
            workspaceId: string;
            includeChildWorkspaces?: boolean;
            includeStores?: boolean;
          }
        | undefined
      >({
        async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
          if (!arg?.workspaceId) {
            return { data: null };
          }
          const params = new URLSearchParams();
          if (arg.includeChildWorkspaces) {
            params.set('includeChildWorkspaces', 'true');
          }
          if (arg.includeStores === false) {
            params.set('includeStores', 'false');
          }
          const queryString = params.toString() ? `?${params.toString()}` : '';
          return (await fetchWithBQ({
            url: `${WorkspaceBaseURL}/${Base64.encode(
              arg.workspaceId
            )}/usage/stores${queryString}`,
          })) as { data: WorkspaceStoresUsageAPIResponse };
        },
        providesTags: ['workspace-usage'],
      }),
    };
  },
});

export default StoerkIdAPI;
